"use client"

import clsx from "clsx"

import type { PropsWithClassName } from "~/@types/generics"
import CollectionSizeGrid from "~/components/ui/Collection/CollectionDefault/components/CollectionSizeGrid"
import { PRODUCTS_PER_PAGE } from "~/components/ui/Collection/CollectionDefault/constants"
import ProductCard from "~/components/ui/Product/ProductCard"
import type { TProductCard } from "~/components/ui/Product/ProductCard/_data/serializer"
import { ProductCardSkeleton } from "~/components/ui/Product/ProductCard/skeleton.client"
import PushEdito from "~/components/ui/PushEdito"
import type { TPushEdito } from "~/components/ui/PushEdito/_data/serializer"
import { useCollectionGridSize } from "~/managers/CollectionManager"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

export type ProductGridProps = PropsWithClassName<{
  products: (TProductCard | TPushEdito)[]
  isSearchPage?: boolean
  collectionId?: string
  isLoading?: boolean
}>

function ProductGrid({ className, products, isSearchPage = false, isLoading = false }: ProductGridProps) {
  const [collectionGridSize] = useCollectionGridSize()

  return (
    <CollectionSizeGrid
      className={clsx(
        className,
        sprinkles({ ...(isSearchPage ? { mB: { mobile: 70, tablet: 60 }, mT: { mobile: 40, tablet: 30 } } : {}) })
      )}
    >
      {!isLoading &&
        products?.map((item, i) => {
          const isPush = "isEdito" in item
          const priority = collectionGridSize === "small" ? i < 4 : i < 3

          return isPush ? (
            <PushEdito key={`push-edito-${i}`} {...item} className={css.GridItem[collectionGridSize]} />
          ) : (
            <ProductCard
              product={item}
              priority={priority}
              key={`product-card-${i}`}
              sliderItemClassName={css.GridItem[collectionGridSize]}
              sizesFromBreakpoint={[{ ratio: 1 }, { ratio: 1 / 3, breakpoint: "md" }]}
              layout={collectionGridSize}
              trackingData={{ index: i + 1, item_list_name: "Collection Page" }}
              productTitleDataAbTest="PLP/ProductTitle"
            />
          )
        })}

      {isLoading &&
        Array.from({ length: PRODUCTS_PER_PAGE })?.map((_, index) => {
          return (
            <ProductCardSkeleton
              key={`products-slider-skeleton-${index}`}
              layout={collectionGridSize}
              className={css.GridItem[collectionGridSize]}
            />
          )
        })}
    </CollectionSizeGrid>
  )
}

export default ProductGrid
